/**
 * ERC-8004 Knowledge Graph Agent Design Tokens
 * Centralized design tokens for consistent styling
 */

export const cambrianColors = {
  bg: {
    primary: 'bg-gray-50 dark:bg-gray-950',
    surface: 'bg-white dark:bg-gray-900',
    code: 'bg-gray-100 dark:bg-gray-800',
    hover: {
      light: 'hover:bg-gray-100/20 dark:hover:bg-gray-800/30',
      medium: 'hover:bg-gray-100/50 dark:hover:bg-gray-800',
      strong: 'hover:bg-gray-100 dark:hover:bg-gray-700',
    },
  },

  border: {
    primary: 'border-gray-200 dark:border-gray-800',
    secondary: 'border-gray-300 dark:border-gray-700',
    brand: 'border-purple-600 dark:border-purple-500',
    focus: 'focus:border-purple-600 dark:focus:border-purple-500',
  },

  text: {
    heading: 'text-gray-900 dark:text-white',
    body: 'text-gray-900 dark:text-gray-200',
    secondary: 'text-gray-600 dark:text-gray-400',
    tertiary: 'text-gray-500 dark:text-gray-500',
    muted: 'text-gray-500 dark:text-gray-400',
    inverse: 'text-white dark:text-gray-900',
  },

  brand: {
    primary: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-600 dark:bg-purple-600',
    bgHover: 'hover:bg-purple-700 dark:hover:bg-purple-700',
    bgLight: 'bg-purple-600/10 dark:bg-purple-600/20',
  },

  // Status colors
  status: {
    success: {
      bg: 'bg-green-50 dark:bg-green-950/30',
      text: 'text-green-700 dark:text-green-400',
    },
    warning: {
      bg: 'bg-orange-50 dark:bg-orange-950/30',
      text: 'text-orange-700 dark:text-orange-400',
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-950/30',
      text: 'text-red-700 dark:text-red-400',
    },
    info: {
      bg: 'bg-blue-50 dark:bg-blue-950/30',
      text: 'text-blue-700 dark:text-blue-400',
    },
  },
} as const;

export const typography = {
  // Headings
  h1: 'text-3xl font-bold tracking-tight',
  h2: 'text-2xl font-semibold tracking-tight',
  h3: 'text-xl font-semibold',
  h4: 'text-lg font-bold',
  h5: 'text-base font-semibold',

  // Body text
  body: 'text-sm',
  bodyLarge: 'text-base',
  small: 'text-xs',

  // Code
  code: 'font-mono text-sm',
  codeInline: 'font-mono text-xs',
} as const;

export const spacing = {
  // Gap spacing
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
  '2xl': 'gap-12',

  // Padding
  padding: {
    xs: 'p-1',
    sm: 'p-2',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8',
  },

  // Margin
  margin: {
    xs: 'm-1',
    sm: 'm-2',
    md: 'm-4',
    lg: 'm-6',
    xl: 'm-8',
  },
} as const;

export const layout = {
  // Container widths
  container: {
    sm: 'max-w-3xl',
    md: 'max-w-5xl',
    lg: 'max-w-7xl',
    full: 'max-w-full',
  },

  // Common layout patterns
  centered: 'mx-auto',
  section: 'px-6 py-8',
  sectionLarge: 'px-6 py-12',

  // Grid layouts
  grid: {
    1: 'grid grid-cols-1',
    2: 'grid grid-cols-1 sm:grid-cols-2',
    3: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    auto: 'grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))]',
  },
} as const;

export const effects = {
  shadow: {
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  },

  rounded: {
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  },

  transition: {
    all: 'transition-all duration-200 ease-in-out',
    colors: 'transition-colors duration-200 ease-in-out',
    transform: 'transition-transform duration-200 ease-in-out',
  },
} as const;

// Common component patterns
export const patterns = {
  card: {
    base: 'rounded-lg border shadow-sm',
    interactive: 'rounded-lg border shadow-sm hover:shadow-md transition-shadow cursor-pointer',
  },

  input: {
    base: 'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2',
  },

  badge: {
    base: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
  },
} as const;
